import React, { useState, useEffect } from 'react'
import {
  Modal,
  Select,
  DatePicker,
  Table,
  Alert,
  Button,
  Divider,
  Input,
} from 'antd'
import moment from 'moment'
import MultiCauseCodePropmt from '../../card-data/MultiCauseCode'
import { DERATE_TYPES, recalcDerivedRows } from './drlUtils'
import { getAeciUnitHourly, getAeciHourlyEvents } from '../aeciApi'
import { dataToRows, eventsToRows } from './rowTransforms'

const { Option } = Select
const HOURS = Array.from({ length: 24 }, (_, i) => i + 1)

const taskToPromise = task =>
  new Promise((resolve, reject) => task.fork(reject, resolve))

const UNALLOCATED_ROW = {
  metric: 'Unallocated',
  rowType: 'metric',
  rowOrder: 6,
}

const AddRemainingDeratesModal = ({
  visible,
  onCancel,
  onApprove,
  selectedDate,
  selectedUtility,
  selectedUnit,
  apiTargetInfo,
  eventTypes,
  causeCodeList,
  saveLoading,
  saveError,
}) => {
  const [startDate, setStartDate] = useState(null)
  const [startHour, setStartHour] = useState(1)
  const [endDate, setEndDate] = useState(null)
  const [endHour, setEndHour] = useState(24)
  const [eventType, setEventType] = useState(null)
  const [causeCode, setCauseCode] = useState('')
  const [selectedCauseCode, setSelectedCauseCode] = useState(null)
  const [causeCodeSearchVisible, setCauseCodeSearchVisible] = useState(false)
  const [causeCodeKeyValues, setCauseCodeKeyValues] = useState(null)
  const [causeCodeValues, setCauseCodeValues] = useState(null)
  const [previewRows, setPreviewRows] = useState([])
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState(null)

  useEffect(() => {
    if (!visible) return
    setStartDate(selectedDate ? selectedDate.clone() : moment())
    setStartHour(1)
    setEndDate(selectedDate ? selectedDate.clone() : moment())
    setEndHour(24)
    setEventType(null)
    setCauseCode('')
    setSelectedCauseCode(null)
    setCauseCodeKeyValues(null)
    setCauseCodeValues(null)
    setPreviewRows([])
    setPreviewError(null)
  }, [visible, selectedDate])

  const derateTypes = (eventTypes || []).filter(e =>
    DERATE_TYPES.has(e.eventType)
  )

  const causeCodeDesc = (() => {
    if (!causeCode || !causeCodeList) return null
    const num = parseInt(
      String(causeCode)
        .split(' - ')[0]
        .trim(),
      10
    )
    if (isNaN(num)) return null
    const match = causeCodeList.find(cc => Number(cc.causeCode) === num)
    return match ? match.description : null
  })()

  const causeCodeValid =
    !!causeCode &&
    (!(causeCodeList && causeCodeList.length > 0) || causeCodeDesc != null)

  const canPreview = startDate && endDate && eventType && causeCodeValid

  const handlePreview = async () => {
    if (!canPreview) return
    setPreviewLoading(true)
    setPreviewError(null)
    setPreviewRows([])

    try {
      const dates = []
      const cur = startDate.clone().startOf('day')
      const endDay = endDate.clone().startOf('day')
      while (!cur.isAfter(endDay)) {
        dates.push(cur.clone())
        cur.add(1, 'day')
      }

      const ccNum = selectedCauseCode
        ? Number(selectedCauseCode.causeCode)
        : Number(causeCode)

      const results = await Promise.all(
        dates.map(async date => {
          const params = {
            unitId: selectedUnit,
            date: date.format('YYYY-MM-DD'),
          }
          const [unitData, events] = await Promise.all([
            taskToPromise(getAeciUnitHourly(apiTargetInfo)(params)),
            taskToPromise(getAeciHourlyEvents(apiTargetInfo)(params)),
          ])
          const realEvents = events.filter(e => e.eventNum >= 0)
          const computed = recalcDerivedRows([
            ...dataToRows(unitData),
            { ...UNALLOCATED_ROW },
            ...eventsToRows(realEvents, causeCodeList),
          ])
          // Build map of existing events at :00 for matching eventType+causeCode
          // key = 1-based hour
          const existingAtZero = {}
          realEvents.forEach(e => {
            if (e.eventType !== eventType) return
            if (Number(e.causeCode) !== ccNum) return
            const m = moment(e.eventStart)
            if (m.minute() !== 0) return
            const h = m.hour() + 1
            existingAtZero[h] = e
          })
          return {
            date,
            unallocRow: computed.find(r => r.metric === 'Unallocated'),
            existingAtZero,
          }
        })
      )

      const rows = []
      results.forEach(({ date, unallocRow, existingAtZero }, idx) => {
        const isFirst = idx === 0
        const isLast = idx === results.length - 1
        const hFrom = isFirst ? startHour : 1
        const hTo = isLast ? endHour : 24
        for (let h = hFrom; h <= hTo; h++) {
          const raw = unallocRow ? unallocRow[`h${h}`] : null
          if (raw == null || raw === '') continue
          const val = parseFloat(raw)
          if (!isNaN(val) && val > 0) {
            const existing = existingAtZero[h]
            const currentAvailCap = existing ? Number(existing.availCap) : null
            const newAvailCap = existing
              ? parseFloat((currentAvailCap + val).toFixed(2))
              : parseFloat(val.toFixed(2))
            rows.push({
              key: `${date.format('MM/DD/YYYY')}-${h}`,
              date: date.format('MM/DD/YYYY'),
              hour: h,
              currentAvailCap,
              unallocated: parseFloat(val.toFixed(2)),
              availCap: newAvailCap,
              action: existing ? 'update' : 'add',
              eventNum: existing ? existing.eventNum : -1,
            })
          }
        }
      })
      setPreviewRows(rows)
    } catch (_err) {
      setPreviewError('Failed to retrieve data for preview')
    } finally {
      setPreviewLoading(false)
    }
  }

  const handleApprove = () => {
    if (!previewRows.length) return
    const unit = selectedUnit ? selectedUnit.substr(3) : selectedUnit
    const payload = previewRows.map(r => ({
      utility: selectedUtility,
      unit,
      eventNum: r.eventNum,
      eventType,
      eventStart: `${r.date} ${String(r.hour - 1).padStart(2, '0')}:00`,
      availCap: r.availCap,
      causeCode: selectedCauseCode
        ? Number(selectedCauseCode.causeCode)
        : Number(causeCode),
      coc: null,
      description: null,
      isSubmitted: null,
    }))
    onApprove(payload)
  }

  const onCauseCodeDialogClose = (keys, values) => {
    setCauseCodeSearchVisible(false)
    setCauseCodeKeyValues(keys)
    setCauseCodeValues(values)
    if (values && values.length > 0) {
      const cc = values[0]
      setSelectedCauseCode(cc)
      setCauseCode(String(cc.causeCode))
      setPreviewRows([])
    }
  }

  const onCauseCodeTreeCancel = keys => {
    setCauseCodeSearchVisible(false)
    setCauseCodeKeyValues(keys)
  }

  const colHeader = {
    fontWeight: 600,
    fontSize: 12,
    color: 'rgba(0,0,0,0.55)',
    paddingBottom: 2,
  }
  const rowTitle = { fontWeight: 500, fontSize: 13, color: 'rgba(0,0,0,0.75)' }

  const previewColumns = [
    { title: 'Date', dataIndex: 'date', width: 100 },
    {
      title: 'Hour',
      dataIndex: 'hour',
      width: 55,
      render: h => String(h).padStart(2, '0'),
    },
    {
      title: 'Current Cap',
      dataIndex: 'currentAvailCap',
      width: 85,
      render: v =>
        v != null ? (
          v.toFixed(2)
        ) : (
          <span style={{ color: 'rgba(0,0,0,0.3)' }}>—</span>
        ),
    },
    {
      title: 'Unallocated',
      dataIndex: 'unallocated',
      width: 85,
      render: v => v.toFixed(2),
    },
    {
      title: 'New Cap',
      dataIndex: 'availCap',
      width: 75,
      render: v => <span style={{ fontWeight: 500 }}>{v.toFixed(2)}</span>,
    },
    {
      title: 'Action',
      dataIndex: 'action',
      width: 65,
      render: a => (
        <span
          style={{
            color: a === 'update' ? '#fa8c16' : '#52c41a',
            fontWeight: 500,
            fontSize: 12,
          }}
        >
          {a === 'update' ? 'Update' : 'Add'}
        </span>
      ),
    },
  ]

  return (
    <>
      <Modal
        visible={visible}
        title="Add Remaining Derates"
        onCancel={onCancel}
        onOk={handleApprove}
        okText="Approve"
        okButtonProps={{
          disabled: previewRows.length === 0,
          loading: saveLoading,
        }}
        width={680}
        maskClosable={false}
        zIndex={101}
      >
        {/* Event Type + Cause Code + Select button — single row */}
        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'flex-end',
            marginBottom: 4,
          }}
        >
          <div style={{ flex: 2 }}>
            <div style={colHeader}>Event Type</div>
            <Select
              value={eventType}
              onChange={val => {
                setEventType(val)
                setPreviewRows([])
              }}
              placeholder="Select derate type"
              style={{ width: '100%' }}
              showSearch
            >
              {derateTypes.map(e => (
                <Option key={e.eventType} value={e.eventType}>
                  {e.eventType} - {e.description}
                </Option>
              ))}
            </Select>
          </div>
          <div style={{ flex: 1 }}>
            <div style={colHeader}>Cause Code</div>
            <Input
              value={causeCode}
              placeholder="Enter or select"
              onChange={e => {
                setCauseCode(e.target.value)
                setSelectedCauseCode({
                  causeCode: e.target.value,
                  description: '',
                })
                setPreviewRows([])
              }}
            />
          </div>
          <Button onClick={() => setCauseCodeSearchVisible(true)}>
            Select
          </Button>
        </div>
        {/* Cause code description / error */}
        <div style={{ marginBottom: 12, minHeight: 18 }}>
          {causeCode && causeCodeDesc && (
            <div style={{ color: '#1890ff', fontSize: 12 }}>
              {causeCodeDesc}
            </div>
          )}
          {causeCode &&
            !causeCodeDesc &&
            causeCodeList &&
            causeCodeList.length > 0 && (
              <div style={{ color: '#f5222d', fontSize: 12 }}>
                Cause code not found
              </div>
            )}
        </div>

        <Divider style={{ margin: '4px 0 12px' }} />

        {/* Date / Hour grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '50px 160px 100px auto',
            columnGap: 12,
            rowGap: 8,
            alignItems: 'center',
            marginBottom: 12,
          }}
        >
          <div />
          <div style={colHeader}>Date</div>
          <div style={colHeader}>Hour</div>
          <div />

          <div style={rowTitle}>Start</div>
          <DatePicker
            value={startDate}
            onChange={v => {
              setStartDate(v)
              setPreviewRows([])
            }}
            allowClear={false}
            format="MM/DD/YYYY"
            size="small"
            style={{ width: '100%' }}
          />
          <Select
            value={startHour}
            onChange={v => {
              setStartHour(v)
              setPreviewRows([])
            }}
            style={{ width: '100%' }}
          >
            {HOURS.map(h => (
              <Option key={h} value={h}>
                {String(h).padStart(2, '0')}
              </Option>
            ))}
          </Select>
          <div />

          <div style={rowTitle}>End</div>
          <DatePicker
            value={endDate}
            onChange={v => {
              setEndDate(v)
              setPreviewRows([])
            }}
            allowClear={false}
            format="MM/DD/YYYY"
            size="small"
            style={{ width: '100%' }}
            disabledDate={d => d && startDate && d.isBefore(startDate, 'day')}
          />
          <Select
            value={endHour}
            onChange={v => {
              setEndHour(v)
              setPreviewRows([])
            }}
            style={{ width: '100%' }}
          >
            {HOURS.map(h => (
              <Option key={h} value={h}>
                {String(h).padStart(2, '0')}
              </Option>
            ))}
          </Select>
          <Button
            type="primary"
            onClick={handlePreview}
            disabled={!canPreview}
            loading={previewLoading}
          >
            Preview
          </Button>
        </div>

        {previewError && (
          <Alert
            type="error"
            message={previewError}
            showIcon
            style={{ marginBottom: 8 }}
          />
        )}

        {!previewLoading && previewRows.length > 0 && (
          <>
            <Divider style={{ margin: '4px 0 10px' }} />
            <Table
              dataSource={previewRows}
              columns={previewColumns}
              rowKey="key"
              size="small"
              pagination={false}
              scroll={{ y: 200 }}
            />
          </>
        )}

        {!previewLoading && previewRows.length === 0 && !previewError && (
          <div
            style={{
              color: 'rgba(0,0,0,0.4)',
              fontSize: 13,
              textAlign: 'center',
              padding: '6px 0',
            }}
          >
            Click Preview to find hours with unallocated capacity
          </div>
        )}

        {saveError && saveError.length > 0 && (
          <div style={{ marginTop: 8 }}>
            {saveError.map((e, i) => (
              <Alert
                key={i}
                type={Number(e.severity) === 0 ? 'warning' : 'error'}
                message={<span dangerouslySetInnerHTML={{ __html: e.error }} />}
                showIcon
                style={{ marginBottom: 4 }}
              />
            ))}
          </div>
        )}
      </Modal>

      {causeCodeSearchVisible && (
        <MultiCauseCodePropmt
          key="addremainingderates-causecodeprompt"
          visibleDialog={causeCodeSearchVisible}
          onDialogClose={onCauseCodeDialogClose}
          onCancelAction={onCauseCodeTreeCancel}
          selectedKeys={causeCodeKeyValues}
          selectedValues={causeCodeValues}
          causeCodeList={causeCodeList}
          causeCodeValue={
            selectedCauseCode ? [selectedCauseCode.causeCode] : []
          }
          unitCodes={[]}
          multiple={false}
          checkable={false}
          zIndex={1200}
          selectable
        />
      )}
    </>
  )
}

export default AddRemainingDeratesModal
